import LOCAL_STORAGE_KEY from '../constants/localStorageKey';
import { DEEPLINK_PARAMS } from '../constants/deeplinkParams';
import { getStorage } from '../utils/general.util';
import { logger } from '../utils/logger';

/**
 * Tracking de AFILIADOS (Affise / Admattic) — distinto de los UTMs y de los
 * click IDs de plataformas de anuncios, aunque se le parezca.
 *
 * La diferencia no es técnica, es contractual: un `gclid` alimenta un reporte,
 * un `sub1` alimenta una FACTURA. El afiliado nos cobra por conversión, así que
 * el ida-y-vuelta tiene que cerrar con el mismo identificador con el que ellos
 * abrieron el clic — y ese identificador solo existe si lo guardamos nosotros.
 *
 * ## Por qué no basta el pixel que mandaron
 *
 * Affise ofrece un `<img>` 1×1 que lee su cookie `afclick` como cookie de
 * TERCERA PARTE desde nuestro dominio. No lo usamos, por tres razones en orden
 * de gravedad:
 *
 *  1. Desde el 2026-07-22 el primer mes de plan es GRATIS: la primera compra
 *     vale $0 y el cargo real llega el mes 2 como renovación de Stripe —
 *     server-side, sin pantalla de éxito. Ningún pixel de navegador puede
 *     dispararse ahí. Es una incapacidad estructural, no un bug.
 *  2. Safari bloquea las cookies de tercera parte y Firefox las aísla. La
 *     campaña es de WhatsApp: móvil, alta cuota de iOS.
 *  3. El usuario da clic en el navegador in-app de WhatsApp, pide su OTP y abre
 *     el SMS → Safari. La cookie se queda en el navegador anterior.
 *
 * El postback con `clickid` EXPLÍCITO no depende de ninguna de las tres: lleva
 * el identificador en la URL, así que no necesita cookie ni navegador
 * compartido. Por eso guardamos `sub1` nosotros y lo mandamos de vuelta.
 *
 * ## Reparto de responsabilidades
 *
 * Esta capa (web-app) cubre la conversión que SÍ pasa por el navegador: el alta
 * de plan. El cargo del mes 2 lo tiene que reportar el backend (motor de
 * ventas), que es el único que lo ve. Las dos mandan al mismo endpoint de
 * Affise con el mismo `clickid`; Affise deduplica por `order_id`.
 */

/** Ventana de atribución del clic de afiliado.
 *
 * 30 días es el estándar de la industria para afiliados y coincide con la
 * ventana del referral propio. NO se alarga a la vida de la cookie de Affise
 * (expira en 2027): pagar comisión por alguien que tocó el anuncio hace ocho
 * meses y se registró por su cuenta es regalar dinero. */
const AFFILIATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type AffiliateClick = {
	/** `sub1` — el id del clic que mintea Affise. Es la llave de la factura. */
	clickId: string;
	/** `sub2` — el publisher id (pid) del afiliado. Sirve para conciliar. */
	pid?: string;
	/** `utm_source` al momento del clic ('admattic'). Lo guardamos AQUÍ y no lo
	 * leemos después de utm-manager porque los UTMs tienen política de
	 * sobreescritura: una visita posterior por otra campaña los cambia, y el
	 * clic de afiliado tiene que seguir sabiendo de quién fue. */
	source?: string;
	capturedAt: number;
};

/** Red de afiliados detrás del `sub1`. Hoy solo hay una; el campo existe para
 * que el evento de conversión no mienta el día que entre la segunda. */
export const AFFILIATE_NETWORK = 'affise';

/** Tipo con el que el clic de afiliado se identifica del lado del backend, tanto
 * en `acquisition.touchpoint` (`click_id_type`) como en `utms_extra` del perfil.
 * Una sola constante para las dos rutas: si divergen, el dato queda guardado con
 * dos nombres y ninguna query los encuentra juntos. */
export const AFFILIATE_CLICK_ID_TYPE = 'affise_clickid';

const parseClick = (raw: string): AffiliateClick | null => {
	try {
		const parsed = JSON.parse(raw);
		if (
			parsed &&
			typeof parsed === 'object' &&
			typeof parsed.clickId === 'string' &&
			parsed.clickId
		) {
			return parsed as AffiliateClick;
		}
	} catch {
	}
	return null;
};

export const clearAffiliateClick = (): void => {
	try {
		getStorage().removeItem(LOCAL_STORAGE_KEY.AFFILIATE_CLICK);
	} catch {
	}
};

/** El clic de afiliado vigente, o null si no hay o ya venció. */
export const readAffiliateClick = (): AffiliateClick | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = getStorage().getItem(LOCAL_STORAGE_KEY.AFFILIATE_CLICK);
		if (!raw) return null;
		const click = parseClick(raw);
		if (!click) return null;
		if (Date.now() - click.capturedAt > AFFILIATE_TTL_MS) {
			logger.info('[affiliate] clic vencido, se descarta', click.clickId);
			clearAffiliateClick();
			return null;
		}
		return click;
	} catch {
		return null;
	}
};

export const persistAffiliateClick = (
	clickId: string,
	extra: { pid?: string; source?: string } = {},
): void => {
	const trimmed = clickId.trim();
	if (!trimmed) return;
	try {
		const click: AffiliateClick = {
			clickId: trimmed,
			...(extra.pid ? { pid: extra.pid } : {}),
			...(extra.source ? { source: extra.source } : {}),
			capturedAt: Date.now(),
		};
		getStorage().setItem(
			LOCAL_STORAGE_KEY.AFFILIATE_CLICK,
			JSON.stringify(click),
		);
		logger.info('[affiliate] clic capturado', {
			clickId: trimmed,
			pid: extra.pid,
			source: extra.source,
		});
	} catch (error) {
		logger.warn('[affiliate] no se pudo guardar el clic', error);
	}
};

/**
 * `?sub1=<click_id>&sub2=<pid>` colgado del landing de la oferta.
 *
 * Se captura en el boot (main.tsx), junto a los UTMs y por la misma razón: el
 * visitante anónimo que abre `/?sub1=…` rebota a /login por el gate de sesión y
 * ese rebote se lleva el query string.
 *
 * `sub1` es un nombre GENÉRICO de Affise, no de Admattic — cualquier oferta de
 * cualquier red montada sobre Affise lo manda. Por eso se guarda bajo un tipo
 * propio (`affise_clickid`) y no como `sub1` pelón: el día que entre la segunda
 * red, el campo ya distingue de quién es cada clic.
 *
 * Se sobreescribe con el ÚLTIMO clic, igual que los click IDs de plataformas:
 * el clic que trajo al usuario en esta visita es el que importa (y el que
 * cobra), no el primero que alguna vez tocó.
 */
export const captureAffiliateClickFromUrl = (): void => {
	if (typeof window === 'undefined') return;
	const params = new URLSearchParams(window.location.search);
	const clickId = params.get(DEEPLINK_PARAMS.AFFILIATE_CLICK_ID);
	if (!clickId) return;
	persistAffiliateClick(clickId, {
		pid: params.get(DEEPLINK_PARAMS.AFFILIATE_PID) ?? undefined,
		source: params.get('utm_source') ?? undefined,
	});
};

// ── Postback ───────────────────────────────────────────────────────

/**
 * Plantilla de postback que nos da la red, VERBATIM, con sus macros entre
 * llaves. Ejemplo:
 *
 *   https://digitalhub.g2afse.com/postback?clickid={clickid}&goal={goal}
 *     &sum={sum}&order_id={order_id}&secure=<token>
 *
 * Es una plantilla y no una URL armada por nosotros a propósito: cada red
 * nombra sus parámetros distinto (`clickid` vs `click_id`, `sum` vs `payout`) y
 * adivinarlos es la forma más fácil de que la conversión se registre como cero.
 * Pegando la suya tal cual, el contrato lo define quien factura.
 *
 * VACÍA POR DEFECTO ⇒ el postback no se dispara. Cargar la URL real es un acto
 * deliberado: cada llamada registra una conversión FACTURABLE en Affise.
 */
const POSTBACK_TEMPLATE = import.meta.env.VITE_AFFISE_POSTBACK_URL ?? '';

/** Con `true` se arma la URL y se registra en consola, pero NO se llama. Es el
 * modo para probar el circuito completo en local sin generar una conversión
 * facturable del lado de ellos. */
const POSTBACK_DRY_RUN =
	String(import.meta.env.VITE_AFFISE_POSTBACK_DRY_RUN ?? '') === 'true';

export type PostbackMacros = {
	/** Qué conversión se reporta ('signup', 'purchase', …). Lo define el
	 * contrato con la red, no nosotros. */
	goal?: string;
	/** Monto en PESOS (no centavos) — lo que Affise llama `sum`. */
	sum?: number;
	/** Nuestro id de orden. Es la llave de deduplicación: la web-app y el
	 * backend pueden reportar la misma compra sin contarla dos veces. */
	orderId?: string | number;
};

/**
 * Sustituye las macros `{nombre}` de la plantilla. Una macro sin valor se
 * reemplaza por cadena vacía en vez de dejarse literal: `sum={sum}` llegando
 * así a Affise se registra como monto 0 o rebota el postback entero.
 */
export const buildPostbackUrl = (
	template: string,
	click: AffiliateClick,
	macros: PostbackMacros = {},
): string => {
	const values: Record<string, string> = {
		clickid: click.clickId,
		click_id: click.clickId,
		pid: click.pid ?? '',
		goal: macros.goal ?? '',
		sum: macros.sum != null ? String(macros.sum) : '',
		order_id: macros.orderId != null ? String(macros.orderId) : '',
		transaction_id: macros.orderId != null ? String(macros.orderId) : '',
	};
	return template.replace(/\{(\w+)\}/g, (literal, key: string) =>
		key in values ? encodeURIComponent(values[key]) : literal,
	);
};

// ── Pixel ──────────────────────────────────────────────────────────

/**
 * Pixel `<img>` de conversión que mandó la red. El que llegó verbatim el
 * 2026-08-18 venía como plantilla sin llenar:
 *
 *   <img src="https://digitalhub.g2afse.com/success.jpg?offer_id=" …/>
 *
 * Se configura como URL completa (con su `offer_id=8792` ya puesto) y admite
 * las mismas macros que el postback, por si la red acepta parámetros extra.
 *
 * **Lo que el pixel NO puede hacer**, y conviene tenerlo presente al leer sus
 * números: no lleva `clickid` ni `order_id`, así que (a) Affise no puede
 * deduplicarlo contra nada, (b) nosotros no podemos conciliar sus líneas contra
 * las nuestras, y (c) depende de la cookie `afclick` de tercera parte, que
 * Safari bloquea y que se pierde al saltar del navegador de WhatsApp al SMS.
 * Sobre todo: **no puede ver el cargo del mes 2**, que con el mes gratis es la
 * venta real. Sirve para arrancar y medir; no para cerrar la contabilidad.
 *
 * VACÍA POR DEFECTO, igual que el postback.
 */
const PIXEL_URL = import.meta.env.VITE_AFFISE_PIXEL_URL ?? '';

/**
 * Resuelve la URL de una red a partir de su config.
 *
 * El valor admite DOS formas, y la diferencia importa en dinero:
 *
 *  - **Mapa por red** (JSON `{"admattic": "…offer_id=8792", "otra": "…offer_id=9001"}`).
 *    Es la forma obligatoria en cuanto hay más de un afiliado: el `offer_id` va
 *    DENTRO de la URL, así que una sola URL global le acreditaría a una red las
 *    conversiones de la otra — y sin que nada lo delate, porque ambas responden
 *    200 y el reporte de cada quien cuadra consigo mismo.
 *  - **URL suelta**, que equivale a `{"*": url}`. Válida con UNA sola red, que
 *    es el estado con el que arrancó Admattic.
 *
 * La llave es el `utm_source` del clic (`AffiliateClick.source`), no el `pid`:
 * el pid es un número de la red y cambia por oferta, mientras que el utm_source
 * es el nombre con el que ya conciliamos en la base.
 *
 * Sin match y sin `*` NO se dispara nada. Es deliberado: ante la duda de a quién
 * acreditarle una conversión, la falla barata es no acreditársela a nadie.
 */
export const resolveNetworkUrl = (
	config: string,
	source: string | undefined,
): string => {
	const raw = config.trim();
	if (!raw) return '';
	if (!raw.startsWith('{')) return raw; // URL suelta = una sola red
	let map: Record<string, unknown>;
	try {
		map = JSON.parse(raw) as Record<string, unknown>;
	} catch {
		logger.warn('[affiliate] config de red ilegible, se ignora');
		return '';
	}
	const bySource = source ? map[source.trim().toLowerCase()] : undefined;
	const value = bySource ?? map['*'];
	return typeof value === 'string' ? value : '';
};

const PIXEL_DRY_RUN =
	String(import.meta.env.VITE_AFFISE_PIXEL_DRY_RUN ?? '') === 'true';

export type PostbackResult =
	| { status: 'sent'; url: string }
	| { status: 'dry-run'; url: string }
	| { status: 'skipped'; reason: 'no-click' | 'not-configured' | 'already-sent' };

/**
 * Guarda de idempotencia PROPIA del postback, keyed por orden.
 *
 * No reusa la guarda de analítica del hook de compra (`charged_tracked_order_id`)
 * a propósito: esa se escribe hasta el final, DESPUÉS de `await trackEvent`, así
 * que si Amplitude falla nunca se marca y una recarga de la pantalla de éxito
 * re-dispararía todo. Para un evento de analítica eso es un duplicado; para un
 * postback es una comisión cobrada dos veces. Se marca ANTES de llamar, porque
 * ante la duda de si salió, la falla barata es no cobrarles de más.
 */
const postbackSentKey = (orderId: string | number): string =>
	`affise_postback_sent:${orderId}`;

/**
 * Reporta la conversión a la red, si hay clic guardado y plantilla configurada.
 *
 * Fire-and-forget con `keepalive`: se dispara en la pantalla de éxito, desde
 * donde el usuario suele navegar de inmediato. Devuelve qué pasó (y la URL
 * armada) para que los tests verifiquen el circuito sin tocar la red.
 *
 * `mode: 'no-cors'` porque el endpoint de Affise no manda CORS headers — no
 * podemos leer la respuesta, y no la necesitamos: el registro de la conversión
 * es del lado de ellos.
 */
export const fireAffiliatePostback = (
	macros: PostbackMacros = {},
	options: { template?: string; dryRun?: boolean } = {},
): PostbackResult => {
	const click = readAffiliateClick();
	if (!click) return { status: 'skipped', reason: 'no-click' };

	const template = options.template ?? POSTBACK_TEMPLATE;
	if (!template) return { status: 'skipped', reason: 'not-configured' };

	const sentKey = macros.orderId != null ? postbackSentKey(macros.orderId) : null;
	if (sentKey && getStorage().getItem(sentKey)) {
		logger.info('[affiliate] postback ya reportado, se omite', macros.orderId);
		return { status: 'skipped', reason: 'already-sent' };
	}

	const url = buildPostbackUrl(template, click, macros);
	const dryRun = options.dryRun ?? POSTBACK_DRY_RUN;

	if (dryRun) {
		logger.info('[affiliate] postback DRY RUN (no se llamó)', url);
		return { status: 'dry-run', url };
	}

	if (sentKey) {
		try {
			getStorage().setItem(sentKey, String(Date.now()));
		} catch {
		}
	}

	try {
		fetch(url, { method: 'GET', mode: 'no-cors', keepalive: true }).catch(
			() => {},
		);
	} catch {
	}
	logger.info('[affiliate] postback enviado', url);
	return { status: 'sent', url };
};

// ── El despachador ─────────────────────────────────────────────────

export type ConversionChannel = 'postback' | 'pixel' | 'none';

export type ConversionResult = {
	channel: ConversionChannel;
	status: PostbackResult['status'];
	url?: string;
	reason?: string;
};

/**
 * Dispara el pixel `<img>` con la cookie de la red. No lleva nuestro click id
 * —el pixel no lo soporta— así que la atribución la resuelve su cookie.
 *
 * `<img>` y no `fetch`: es el contrato que la red publica, sobrevive mejor a la
 * navegación inmediata (el navegador termina la carga aunque la página se vaya)
 * y no necesita CORS. El resultado no se puede leer: el registro es de su lado.
 */
const firePixel = (
	macros: PostbackMacros,
	options: { url?: string; dryRun?: boolean } = {},
): PostbackResult => {
	const click = readAffiliateClick();
	// Se exige clic guardado aunque el pixel no lo use. Sin esta guarda el pixel
	// dispararía en TODA compra, y le acreditaría a la red cualquier usuario que
	// alguna vez tocó un link de Affise —de esta oferta o de otra— y llegó a
	// heru por su cuenta meses después. Pagaríamos por tráfico que no trajeron.
	if (!click) return { status: 'skipped', reason: 'no-click' };

	const base = options.url ?? resolveNetworkUrl(PIXEL_URL, click.source);
	if (!base) return { status: 'skipped', reason: 'not-configured' };

	const sentKey = macros.orderId != null ? postbackSentKey(macros.orderId) : null;
	if (sentKey && getStorage().getItem(sentKey)) {
		logger.info('[affiliate] conversión ya reportada, se omite', macros.orderId);
		return { status: 'skipped', reason: 'already-sent' };
	}

	const url = buildPostbackUrl(base, click, macros);

	if (options.dryRun ?? PIXEL_DRY_RUN) {
		logger.info('[affiliate] pixel DRY RUN (no se cargó)', url);
		return { status: 'dry-run', url };
	}

	if (sentKey) {
		try {
			getStorage().setItem(sentKey, String(Date.now()));
		} catch {
		}
	}

	try {
		const img = new Image(1, 1);
		img.referrerPolicy = 'no-referrer-when-downgrade';
		img.src = url;
	} catch {
	}
	logger.info('[affiliate] pixel disparado', url);
	return { status: 'sent', url };
};

/**
 * Reporta UNA conversión a la red por UN canal, nunca dos.
 *
 * Esta es la guarda que importa. El pixel no lleva `order_id`, así que Affise
 * **no puede deduplicarlo** contra el postback: si los dos salieran por la misma
 * venta se registrarían dos conversiones y nos cobrarían dos comisiones, sin
 * forma de detectarlo hasta la conciliación del mes siguiente. Por eso el canal
 * se elige aquí, y es excluyente.
 *
 * Cuando ambos están configurados gana el **postback**: es el único que lleva
 * nuestro click id y nuestro id de orden, o sea el único cuyas líneas se pueden
 * cruzar contra nuestra base antes de pagar. El pixel es el modo de arranque
 * mientras la red no manda su URL de postback.
 */
export const reportAffiliateConversion = (
	macros: PostbackMacros = {},
	options: {
		postbackTemplate?: string;
		pixelUrl?: string;
		dryRun?: boolean;
	} = {},
): ConversionResult => {
	const click = readAffiliateClick();
	const template =
		options.postbackTemplate ?? resolveNetworkUrl(POSTBACK_TEMPLATE, click?.source);
	if (template) {
		const r = fireAffiliatePostback(macros, {
			template,
			...(options.dryRun != null ? { dryRun: options.dryRun } : {}),
		});
		return { channel: 'postback', ...r };
	}

	const pixelUrl = options.pixelUrl ?? resolveNetworkUrl(PIXEL_URL, click?.source);
	if (pixelUrl) {
		const r = firePixel(macros, {
			url: pixelUrl,
			...(options.dryRun != null ? { dryRun: options.dryRun } : {}),
		});
		return { channel: 'pixel', ...r };
	}

	return { channel: 'none', status: 'skipped', reason: 'not-configured' };
};
